import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, ArrowRight, UserPlus, Search, PenLine, Send, ChevronLeft } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { PRODUCT_FAMILIES, PRODUCTS, findProduct } from "../forms/productCatalog";

/**
 * CreateApplicationModal — a two-step wizard:
 *   Step 1  Pick an existing customer OR create a new one on the spot.
 *   Step 2  Choose who fills it in: Client (private link) or Admin (open editor).
 *
 * On completion:
 *   - "Client" => shows a copy-able private link and closes.
 *   - "Admin"  => opens /apply/{token}#admin in a new tab so the admin can type
 *                 the answers themselves and sign later.
 *
 * Props:
 *   onClose    () => void
 *   onCreated  (createdForm, { customerCreated }) => void   // for the parent to reload
 *   preselectedCustomerId  optional, locks the customer picker (used from a
 *                           customer's own Applications tab).
 */
export const CreateApplicationModal = ({ onClose, onCreated, preselectedCustomerId = null }) => {
  const [step, setStep] = useState(1); // 1 pick product, 2 pick customer, 3 pick filler, 4 link shown
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState(preselectedCustomerId || "");
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [newCust, setNewCust] = useState({ name: "", company: "", email: "", address: "" });
  const [type, setType] = useState("");          // product key — chosen in step 1
  const [familyId, setFamilyId] = useState(""); // currently-expanded family tile
  const [working, setWorking] = useState(false);
  const [createdForm, setCreatedForm] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/customers`, authHeaders())
      .then(({ data }) => setCustomers(data || []))
      .catch(() => setCustomers([]));
  }, []);

  const selectedCustomer = (customers || []).find((c) => c.id === customerId);

  const filtered = (customers || [])
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Step 1 → Step 2: either confirm the picked customer or create a new one.
  const nextFromStep1 = async () => {
    setWorking(true);
    try {
      let cid = customerId;
      let created = false;
      if (mode === "new") {
        const name = newCust.name.trim();
        if (!name) {
          toast.error("Give the customer a name.");
          setWorking(false);
          return;
        }
        const { data: c } = await axios.post(`${API}/admin/customers`, {
          name,
          company: newCust.company || "",
          email: newCust.email || "",
          address: newCust.address || "",
        }, authHeaders());
        cid = c.id;
        created = true;
        setCustomerId(cid);
      }
      if (!cid) {
        toast.error("Pick a customer first.");
        setWorking(false);
        return;
      }
      // Store the "customer was just created" flag for the parent so it can refresh its list.
      window.__appCreated = created;
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save customer");
    } finally {
      setWorking(false);
    }
  };

  const createForm = async (filler) => {
    setWorking(true);
    try {
      const name = selectedCustomer?.name || newCust.name || "";
      const { data } = await axios.post(`${API}/admin/forms`, {
        type,
        customer_name: name,
        customer_id: customerId,
      }, authHeaders());
      setCreatedForm(data);
      if (filler === "admin") {
        window.open(`/apply/${data.token}#admin`, "_blank", "noopener");
        toast.success("Application opened in a new tab.");
        onCreated?.(data, { customerCreated: !!window.__appCreated });
        onClose();
      } else {
        setStep(4);
        onCreated?.(data, { customerCreated: !!window.__appCreated });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create application");
    } finally {
      setWorking(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/apply/${createdForm.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        data-testid="create-application-modal"
        className="bg-white border border-forest/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.2)] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-forest/10 sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Create application</p>
            <h3 className="font-serif text-xl text-forest mt-1">
              {step === 1 && "What is this application for?"}
              {step === 2 && "Who is this application for?"}
              {step === 3 && "Who will fill it in?"}
              {step === 4 && "Share this private link"}
            </h3>
            {step > 1 && type && (
              <p className="text-[11px] text-sage mt-1">{findProduct(type).label}</p>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="create-application-close" className="p-2 text-forest/60 hover:text-forest"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Step 1 — Product picker, grouped by family */}
          {step === 1 && (
            <>
              <p className="text-[13px] text-sage">Pick the product first — the application will only ask the questions that apply to it.</p>
              {!familyId ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="create-application-family-grid">
                  {PRODUCT_FAMILIES.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFamilyId(f.id)}
                      data-testid={`create-application-family-${f.id}`}
                      className="text-left px-4 py-3 border border-forest/15 bg-cream-3 hover:border-gold hover:bg-cream rounded-lg transition-colors"
                    >
                      <p className="font-serif text-[15px] text-forest">{f.label}</p>
                      <p className="text-[11.5px] text-sage mt-0.5 leading-snug">{f.sub}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setFamilyId("")}
                    data-testid="create-application-family-back"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-forest/60 hover:text-forest"
                  ><ChevronLeft size={12} /> All families</button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="create-application-product-grid">
                    {PRODUCTS.filter((p) => p.family === familyId).map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setType(p.key)}
                        data-testid={`create-application-product-${p.key}`}
                        className={`text-left px-4 py-3 border rounded-lg transition-colors ${
                          type === p.key
                            ? "border-gold bg-gold/10"
                            : "border-forest/15 bg-white hover:border-forest/40"
                        }`}
                      >
                        <p className="font-semibold text-[13.5px] text-forest leading-snug">{p.label}</p>
                        {type === p.key && (
                          <p className="text-[10px] tracking-[0.14em] uppercase font-bold text-gold-dark mt-1">Selected</p>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!type}
                  data-testid="create-application-product-next"
                  className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
                >Continue <ArrowRight size={14} /></button>
              </div>
            </>
          )}

          {/* Step 2 — Customer picker */}
          {step === 2 && (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode("existing")}
                  data-testid="create-application-mode-existing"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-[12px] font-bold tracking-[0.14em] uppercase transition-colors ${
                    mode === "existing" ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15 hover:border-forest/40"
                  }`}
                ><Search size={14} /> Existing customer</button>
                <button
                  type="button"
                  onClick={() => setMode("new")}
                  data-testid="create-application-mode-new"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border rounded-lg text-[12px] font-bold tracking-[0.14em] uppercase transition-colors ${
                    mode === "new" ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15 hover:border-forest/40"
                  }`}
                ><UserPlus size={14} /> New customer</button>
              </div>

              {mode === "existing" ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, company or email…"
                    data-testid="create-application-search"
                    className="w-full bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none"
                  />
                  <div className="border border-forest/10 rounded-lg max-h-72 overflow-y-auto divide-y divide-forest/5" data-testid="create-application-customer-list">
                    {customers === null && <p className="px-4 py-6 text-sm text-sage">Loading…</p>}
                    {customers && filtered.length === 0 && (
                      <p className="px-4 py-6 text-sm text-sage text-center">No matches. Try "New customer" instead.</p>
                    )}
                    {filtered.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setCustomerId(c.id)}
                        data-testid={`create-application-customer-${c.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                          customerId === c.id ? "bg-gold-soft/20" : "hover:bg-cream-3"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-forest truncate">{c.name}</p>
                          <p className="text-[11px] text-sage mt-0.5 truncate">{[c.company, c.email].filter(Boolean).join(" · ") || "—"}</p>
                        </div>
                        {customerId === c.id && <span className="text-[10px] tracking-[0.14em] uppercase font-bold text-gold-dark">Selected</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <input type="text" value={newCust.name} onChange={(e) => setNewCust((n) => ({ ...n, name: e.target.value }))} placeholder="Full name" data-testid="create-application-newcust-name" className="w-full bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" value={newCust.company} onChange={(e) => setNewCust((n) => ({ ...n, company: e.target.value }))} placeholder="Company (optional)" data-testid="create-application-newcust-company" className="bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    <input type="email" value={newCust.email} onChange={(e) => setNewCust((n) => ({ ...n, email: e.target.value }))} placeholder="Email (optional)" data-testid="create-application-newcust-email" className="bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">Product</label>
                <div className="bg-cream-3 border border-forest/15 rounded-lg px-4 py-2.5 text-sm text-forest flex items-center justify-between">
                  <span>{findProduct(type).label}</span>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    data-testid="create-application-change-product"
                    className="text-[10px] tracking-[0.14em] uppercase font-bold text-gold-dark hover:text-gold"
                  >Change</button>
                </div>
              </div>

              <div className="flex gap-2 justify-between pt-2">
                <button type="button" onClick={() => setStep(1)} className="btn-ghost-dark inline-flex items-center gap-1"><ChevronLeft size={14} /> Back</button>
                <div className="flex gap-2">
                  <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
                  <button
                    type="button"
                    onClick={nextFromStep1}
                    disabled={working || (mode === "existing" ? !customerId : !newCust.name.trim())}
                    data-testid="create-application-next"
                    className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    Continue <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Step 3 — Filler picker */}
          {step === 3 && (
            <>
              <p className="text-[13px] text-sage">
                <span className="font-semibold text-forest">{findProduct(type).label}</span> application for <span className="font-semibold text-forest">{selectedCustomer?.name || newCust.name}</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => createForm("client")}
                  disabled={working}
                  data-testid="create-application-filler-client"
                  className="text-left bg-cream-3 hover:bg-cream border border-forest/10 hover:border-gold rounded-xl p-5 transition-colors disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-white border border-gold/40 text-gold flex items-center justify-center mb-3"><Send size={16} /></div>
                  <p className="font-serif text-lg text-forest">Client fills it in</p>
                  <p className="text-[12px] text-sage mt-1 leading-relaxed">Create a private link. Share it with the client — they save as they go and sign on any device.</p>
                </button>
                <button
                  type="button"
                  onClick={() => createForm("admin")}
                  disabled={working}
                  data-testid="create-application-filler-admin"
                  className="text-left bg-cream-3 hover:bg-cream border border-forest/10 hover:border-gold rounded-xl p-5 transition-colors disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-white border border-gold/40 text-gold flex items-center justify-center mb-3"><PenLine size={16} /></div>
                  <p className="font-serif text-lg text-forest">I will fill it in</p>
                  <p className="text-[12px] text-sage mt-1 leading-relaxed">Open the application right now. Type the answers yourself, save and continue later, and sign when the client is in front of you.</p>
                </button>
              </div>
              <div className="flex gap-2 justify-between pt-2">
                <button type="button" onClick={() => setStep(2)} className="btn-ghost-dark inline-flex items-center gap-1"><ChevronLeft size={14} /> Back</button>
                <button type="button" onClick={onClose} className="btn-ghost-dark">Cancel</button>
              </div>
            </>
          )}

          {/* Step 4 — Share link */}
          {step === 4 && createdForm && (
            <>
              <p className="text-[13px] text-sage">
                The application is created. Send the private link below to <span className="font-semibold text-forest">{selectedCustomer?.name || newCust.name}</span>.
              </p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={`${window.location.origin}/apply/${createdForm.token}`}
                  data-testid="create-application-link-url"
                  className="flex-1 bg-cream-3 border border-forest/15 rounded-lg px-3 py-2.5 text-[13px] font-mono focus:border-gold focus:outline-none"
                />
                <button type="button" onClick={copyLink} data-testid="create-application-link-copy" className="btn-forest !py-2.5 inline-flex items-center gap-1.5"><Copy size={14} /> Copy</button>
              </div>
              <p className="text-[11px] text-sage">The application will show as <span className="font-semibold text-forest">In progress</span> under {selectedCustomer?.name || newCust.name}'s Applications until the client signs.</p>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} className="btn-gold">Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
